import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Shield, Eye, Lock, FileText } from "lucide-react";

const Datenschutz = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Datenschutz – Malerfachbetrieb Stein Herford"
        description="Datenschutzerklärung der Malerfachbetrieb Stein GmbH & Co. KG – Informationen zum Umgang mit personenbezogenen Daten gemäß DSGVO."
        canonical="https://malerfachbetrieb-stein.de/datenschutz"
      />
      <Navigation />
      
      <main className="pt-32 pb-24">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-16 text-center animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 border border-foreground/20 bg-foreground/5 rounded-full">
                <Shield className="h-4 w-4" />
                <span className="text-xs tracking-widest uppercase">DSGVO-konform</span>
              </div>
              <h1 className="font-caslon text-4xl md:text-6xl font-bold mb-6">
                Datenschutzerklärung
              </h1>
              <div className="w-24 h-px bg-foreground mx-auto" />
            </div>

            {/* Table of Contents */}
            <div className="bg-muted/30 rounded-3xl p-8 mb-12 animate-fade-in-up delay-200">
              <h2 className="font-caslon text-xl font-bold mb-4">Inhalt</h2>
              <ol className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#abschnitt-1" className="hover:text-foreground transition-colors">1. Datenschutz auf einen Blick</a></li>
                <li><a href="#abschnitt-2" className="hover:text-foreground transition-colors">2. Hosting</a></li>
                <li><a href="#abschnitt-3" className="hover:text-foreground transition-colors">3. Allgemeine Hinweise und Pflichtinformationen</a></li>
                <li><a href="#abschnitt-4" className="hover:text-foreground transition-colors">4. Datenerfassung auf dieser Website</a></li>
                <li><a href="#abschnitt-5" className="hover:text-foreground transition-colors">5. Soziale Medien</a></li>
                <li><a href="#abschnitt-6" className="hover:text-foreground transition-colors">6. Analyse-Tools und Werbung</a></li>
                <li><a href="#abschnitt-7" className="hover:text-foreground transition-colors">7. Plugins und Tools</a></li>
              </ol>
            </div>

            {/* Content Sections */}
            <div className="space-y-8 animate-fade-in-up delay-400">
              
              {/* Section 1 */}
              <section id="abschnitt-1" className="bg-background border border-foreground/10 rounded-3xl p-8 scroll-mt-32">
                <h2 className="font-caslon text-2xl font-bold mb-6 flex items-center gap-3">
                  <Eye className="h-6 w-6" />
                  1. Datenschutz auf einen Blick
                </h2>
                
                <h3 className="font-semibold text-lg mb-3">Allgemeine Hinweise</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen 
                  Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen 
                  Sie persönlich identifiziert werden können. Ausführliche Informationen zum Thema Datenschutz 
                  entnehmen Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.
                </p>

                <h3 className="font-semibold text-lg mb-3">Datenerfassung auf dieser Website</h3>
                <h4 className="font-semibold mb-2">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h4>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten 
                  können Sie dem Abschnitt &quot;Hinweis zur Verantwortlichen Stelle&quot; in dieser Datenschutzerklärung entnehmen.
                </p>

                <h4 className="font-semibold mb-2">Wie erfassen wir Ihre Daten?</h4>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich 
                  z. B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch unsere 
                  IT-Systeme erfasst. Das sind vor allem technische Daten (z. B. Internetbrowser, Betriebssystem oder 
                  Uhrzeit des Seitenaufrufs). Die Erfassung dieser Daten erfolgt automatisch, sobald Sie diese Website betreten.
                </p>

                <h4 className="font-semibold mb-2">Wofür nutzen wir Ihre Daten?</h4>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. 
                  Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.
                </p>

                <h4 className="font-semibold mb-2">Welche Rechte haben Sie bezüglich Ihrer Daten?</h4>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer 
                  gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder 
                  Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt haben, 
                  können Sie diese Einwilligung jederzeit für die Zukunft widerrufen. Außerdem haben Sie das Recht, unter 
                  bestimmten Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. Des 
                  Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit an uns wenden.
                </p>

                <h4 className="font-semibold mb-2">Analyse-Tools und Tools von Drittanbietern</h4>
                <p className="text-muted-foreground leading-relaxed">
                  Beim Besuch dieser Website kann Ihr Surf-Verhalten statistisch ausgewertet werden. Das geschieht vor 
                  allem mit sogenannten Analyseprogrammen. Detaillierte Informationen zu diesen Analyseprogrammen finden 
                  Sie in der folgenden Datenschutzerklärung.
                </p>
              </section>

              {/* Section 2 */}
              <section id="abschnitt-2" className="bg-background border border-foreground/10 rounded-3xl p-8 scroll-mt-32">
                <h2 className="font-caslon text-2xl font-bold mb-6">2. Hosting</h2>
                
                <h3 className="font-semibold text-lg mb-3">Externes Hosting</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Diese Website wird bei einem externen Dienstleister gehostet (Hoster). Die personenbezogenen Daten, die 
                  auf dieser Website erfasst werden, werden auf den Servern des Hosters gespeichert. Hierbei kann es sich 
                  v.a. um IP-Adressen, Kontaktanfragen, Meta- und Kommunikationsdaten, Vertragsdaten, Kontaktdaten, Namen, 
                  Websitezugriffe und sonstige Daten, die über eine Website generiert werden, handeln.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Der Einsatz des Hosters erfolgt zum Zwecke der Vertragserfüllung gegenüber unseren potenziellen und 
                  bestehenden Kunden (Art. 6 Abs. 1 lit. b DSGVO) und im Interesse einer sicheren, schnellen und effizienten 
                  Bereitstellung unseres Online-Angebots durch einen professionellen Anbieter (Art. 6 Abs. 1 lit. f DSGVO).
                </p>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Unser Hoster wird Ihre Daten nur insoweit verarbeiten, wie dies zur Erfüllung seiner Leistungspflichten 
                  erforderlich ist und unsere Weisungen in Bezug auf diese Daten befolgen.
                </p>

                <div className="bg-muted/30 p-6 rounded-2xl mb-6">
                  <p className="font-semibold mb-2">Wir setzen folgenden Hoster ein:</p>
                  <p className="text-muted-foreground">sevmatic Franz & Klassen GbR</p>
                  <p className="text-muted-foreground">Wilhelm-Martin-Str. 4</p>
                  <p className="text-muted-foreground">D-76356 Weingarten (Baden)</p>
                </div>

                <h4 className="font-semibold mb-2">Abschluss eines Vertrages über Auftragsverarbeitung</h4>
                <p className="text-muted-foreground leading-relaxed">
                  Um die datenschutzkonforme Verarbeitung zu gewährleisten, haben wir einen Vertrag über Auftragsverarbeitung 
                  mit unserem Hoster geschlossen.
                </p>
              </section>

              {/* Section 3 - Splitting into smaller subsections for better readability */}
              <section id="abschnitt-3" className="bg-background border border-foreground/10 rounded-3xl p-8 scroll-mt-32">
                <h2 className="font-caslon text-2xl font-bold mb-6 flex items-center gap-3">
                  <Lock className="h-6 w-6" />
                  3. Allgemeine Hinweise und Pflichtinformationen
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-lg mb-3">Datenschutz</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre 
                      personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie 
                      dieser Datenschutzerklärung.
                    </p>
                  </div>

                  <div className="bg-muted/30 p-6 rounded-2xl">
                    <h4 className="font-semibold mb-3">Hinweis zur verantwortlichen Stelle</h4>
                    <p className="text-muted-foreground mb-4">Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:</p>
                    <p className="font-semibold">Malerfachbetrieb Stein GmbH & Co.KG</p>
                    <p className="text-muted-foreground">Fütiger Str. 15, 32699 Extertal</p>
                    <p className="text-muted-foreground mt-2">Telefon: 0525 84 99 03 4</p>
                    <p className="text-muted-foreground">E-Mail: info@malerfachbetrieb-stein.de</p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg mb-3">Ihre Rechte</h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li className="flex gap-2">
                        <span className="text-foreground">•</span>
                        <span>Recht auf Auskunft über Ihre gespeicherten Daten</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-foreground">•</span>
                        <span>Recht auf Berichtigung unrichtiger Daten</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-foreground">•</span>
                        <span>Recht auf Löschung Ihrer Daten</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-foreground">•</span>
                        <span>Recht auf Einschränkung der Verarbeitung</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-foreground">•</span>
                        <span>Recht auf Datenübertragbarkeit</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-foreground">•</span>
                        <span>Recht auf Widerspruch gegen die Verarbeitung</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-foreground">•</span>
                        <span>Beschwerderecht bei einer Aufsichtsbehörde</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg mb-3">SSL- bzw. TLS-Verschlüsselung</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte, wie zum 
                      Beispiel Bestellungen oder Anfragen, die Sie an uns als Seitenbetreiber senden, eine SSL- bzw. 
                      TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers 
                      von &quot;http://&quot; auf &quot;https://&quot; wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 4 */}
              <section id="abschnitt-4" className="bg-background border border-foreground/10 rounded-3xl p-8 scroll-mt-32">
                <h2 className="font-caslon text-2xl font-bold mb-6 flex items-center gap-3">
                  <FileText className="h-6 w-6" />
                  4. Datenerfassung auf dieser Website
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-lg mb-3">Cookies</h3>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Unsere Internetseiten verwenden so genannte &quot;Cookies&quot;. Cookies sind kleine Datenpakete und richten auf 
                      Ihrem Endgerät keinen Schaden an. Sie werden entweder vorübergehend für die Dauer einer Sitzung 
                      (Session-Cookies) oder dauerhaft (permanente Cookies) auf Ihrem Endgerät gespeichert.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      Cookies haben verschiedene Funktionen. Zahlreiche Cookies sind technisch notwendig, da bestimmte 
                      Webseitenfunktionen ohne diese nicht funktionieren würden. Andere Cookies können zur Auswertung des 
                      Nutzerverhaltens oder zu Werbezwecken verwendet werden.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg mb-3">Kontaktformular</h3>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular 
                      inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall 
                      von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage 
                      mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen 
                      erforderlich ist.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg mb-3">Anfrage per E-Mail, Telefon oder Telefax</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Wenn Sie uns per E-Mail, Telefon oder Telefax kontaktieren, wird Ihre Anfrage inklusive aller daraus 
                      hervorgehenden personenbezogenen Daten (Name, Anfrage) zum Zwecke der Bearbeitung Ihres Anliegens bei 
                      uns gespeichert und verarbeitet. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 5 */}
              <section id="abschnitt-5" className="bg-background border border-foreground/10 rounded-3xl p-8 scroll-mt-32">
                <h2 className="font-caslon text-2xl font-bold mb-6">5. Soziale Medien</h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-lg mb-3">Facebook</h3>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Auf dieser Website sind Elemente des sozialen Netzwerks Facebook integriert. Anbieter dieses Dienstes 
                      ist die Meta Platforms Ireland Limited, 4 Grand Canal Square, Dublin 2, Irland.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      Wenn das Social-Media-Element aktiv ist, wird eine direkte Verbindung zwischen Ihrem Endgerät und dem 
                      Facebook-Server hergestellt. Facebook erhält dadurch die Information, dass Sie mit Ihrer IP-Adresse diese 
                      Website besucht haben.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg mb-3">Instagram</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Auf dieser Website sind Funktionen des Dienstes Instagram eingebunden. Diese Funktionen werden angeboten 
                      durch die Meta Platforms Ireland Limited, 4 Grand Canal Square, Grand Canal Harbour, Dublin 2, Irland.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 6 */}
              <section id="abschnitt-6" className="bg-background border border-foreground/10 rounded-3xl p-8 scroll-mt-32">
                <h2 className="font-caslon text-2xl font-bold mb-6">6. Analyse-Tools und Werbung</h2>

                <div>
                  <h3 className="font-semibold text-lg mb-3">WP Statistics</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Diese Website nutzt das Analysetool WP Statistics, um Besucherzugriffe statistisch auszuwerten. Anbieter 
                    ist Veronalabs, Tatari 64, 10134, Tallinn, Estland.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Mit WP Statistics können wir die Nutzung unserer Website analysieren. Die mit WP Statistics erfassten Daten 
                    werden ausschließlich auf unserem eigenen Server gespeichert.
                  </p>
                </div>
              </section>

              {/* Section 7 */}
              <section id="abschnitt-7" className="bg-background border border-foreground/10 rounded-3xl p-8 scroll-mt-32">
                <h2 className="font-caslon text-2xl font-bold mb-6">7. Plugins und Tools</h2>

                <div>
                  <h3 className="font-semibold text-lg mb-3">Google Fonts (lokales Hosting)</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Diese Seite nutzt zur einheitlichen Darstellung von Schriftarten so genannte Google Fonts, die von Google 
                    bereitgestellt werden. Die Google Fonts sind lokal installiert. Eine Verbindung zu Servern von Google findet 
                    dabei nicht statt.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Weitere Informationen zu Google Fonts finden Sie unter https://developers.google.com/fonts/faq und in der 
                    Datenschutzerklärung von Google: https://policies.google.com/privacy?hl=de.
                  </p>
                </div>

                <div className="mt-8 pt-6 border-t border-border/50">
                  <p className="text-xs text-muted-foreground">
                    Quelle: <a href="https://www.e-recht24.de" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">https://www.e-recht24.de</a>
                  </p>
                </div>
              </section>
            </div>

            {/* Back to Home */}
            <div className="mt-12 text-center">
              <a 
                href="/"
                className="inline-flex items-center gap-2 px-8 py-4 bg-foreground text-background rounded-2xl hover:bg-foreground/90 transition-all duration-300 hover:scale-105 font-medium"
              >
                Zurück zur Startseite
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Datenschutz;
